/* iter-125 — InlineRoiField.
 *
 * Click-to-edit ROI box on the public AreaDetailDialog. Editors and
 * admins see a hover affordance + Pencil icon; clicking opens an inline
 * textarea that saves on blur (or Cmd/Ctrl+Enter). Non-editors get the
 * normal read-only formatted display.
 *
 * Persists via PUT /api/admin/areas/{id} with just the changed field.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { displayMoney } from '../lib/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EDITOR_LIKE = ['editor', 'admin', 'engineer', 'ownership', 'top_leader'];

/**
 *  - areaId:        Required. The area document's id (e.g. 'porte-cochere').
 *  - field:         Required. The DB field name (e.g. 'roi_investment').
 *  - value:         The current value (string).
 *  - label:         Section label rendered above the value.
 *  - isMoney:       If true, runs the value through displayMoney() for display.
 *  - onSaved:       Callback({ field, value }) after a successful save.
 *  - placeholder:   Placeholder text when entering edit mode.
 */
export default function InlineRoiField({
  areaId,
  field,
  value,
  label,
  isMoney = false,
  onSaved,
  placeholder = '',
  testid,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const role = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '').toLowerCase() : '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const canEdit = EDITOR_LIKE.includes(role) && !!token;

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select?.();
    }
  }, [editing]);

  const save = async () => {
    if (saving) return;
    if ((draft || '') === (value || '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/areas/${areaId}`, { [field]: draft }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved?.({ field, value: draft });
      setEditing(false);
      toast.success(`${label} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
      setDraft(value || '');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      setDraft(value || '');
      setEditing(false);
    }
  };

  const displayValue = isMoney ? displayMoney(value) : value;

  if (editing) {
    return (
      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#92400E', fontFamily: 'Manrope' }}>{label}</p>
        <textarea
          ref={ref}
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="w-full text-xs leading-snug px-2 py-1.5 rounded outline-none resize-y"
          style={{
            fontFamily: 'Manrope',
            color: '#1A2E2E',
            background: '#FFF7E6',
            border: '1px solid #F59E0B',
          }}
          data-testid={`${testid}-input`}
        />
        {saving && <Loader2 className="absolute top-1.5 right-1.5 w-3.5 h-3.5 animate-spin" style={{ color: '#92400E' }} />}
      </div>
    );
  }

  return (
    <div
      className={`group relative ${canEdit ? 'cursor-pointer' : ''}`}
      onClick={() => canEdit && setEditing(true)}
      data-testid={testid}
      title={canEdit ? 'Click to edit' : undefined}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#92400E', fontFamily: 'Manrope' }}>{label}</p>
      <p className="text-xs leading-snug whitespace-pre-wrap" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
        {displayValue || (canEdit ? <span className="italic opacity-60">Click to add…</span> : '—')}
      </p>
      {canEdit && (
        <Pencil
          className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity"
          style={{ color: '#92400E' }}
        />
      )}
    </div>
  );
}
